/**
 * Motor sessie 8H: de regel V8 naast de AI-kosten.
 *
 * Beurten, handen en kosten van de nieuwe motor voor vandaag, de laatste
 * 7 en de laatste 30 dagen (Amsterdamse kalenderdagen tot en met vandaag),
 * zonder testbeurten. De datums staan bij elke periode, zodat er nooit
 * getwijfeld wordt wat "maand" betekent. Mislukte beurten staan er met
 * het woord bij, nooit alleen als kleur. Zonder beurten een eerlijke lege
 * stand. Eigen verzoek: een trage telling laat de rest van de pagina staan.
 */
import { useQuery } from "@tanstack/react-query";

import { v8GezondheidApi, type V8Periode } from "@/lib/api";

import { UitlegTip } from "./UitlegTip";

const UITLEG =
  "Beurten die het portaal naar V8 stuurde (klaar, naar JP, mislukt of nog bezig), zonder testbeurten. " +
  "Beurten die bewust niet naar V8 gingen (blind, niet ingesteld, geen klantvraag) tellen niet. " +
  "Handen zijn de acties die V8 in die beurten vroeg. Kosten zijn wat V8 per beurt meldde; " +
  "een beurt zonder bedrag telt voor nul euro.";

function euro(waarde: number): string {
  return waarde.toLocaleString("nl-NL", { style: "currency", currency: "EUR" });
}

function datum(iso: string): string {
  return new Date(`${iso}T12:00:00`).toLocaleDateString("nl-NL", {
    day: "numeric",
    month: "long",
  });
}

function bereik(periode: V8Periode): string {
  return periode.begin === periode.eind
    ? datum(periode.eind)
    : `${datum(periode.begin)} t/m ${datum(periode.eind)}`;
}

function tijd(iso: string): string {
  return new Date(iso).toLocaleTimeString("nl-NL", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function V8Regel() {
  const v8 = useQuery({
    queryKey: ["werkplek", "gezondheid", "v8"],
    queryFn: v8GezondheidApi.haal,
  });
  const data = v8.data;
  const leeg =
    !!data && data.perioden.every((periode) => periode.beurten === 0);

  return (
    <div className="wp-blok" data-testid="v8-regel">
      <h4>
        <span>V8, nieuwe motor</span>
        {data ? (
          <span className="wp-gz-mk-onder">
            bijgewerkt {tijd(data.bijgewerkt_op)}
          </span>
        ) : null}
      </h4>
      {v8.isLoading ? (
        <p className="wp-gz-uitleg">Bezig met laden…</p>
      ) : v8.isError || !data ? (
        <p className="wp-gz-uitleg" style={{ color: "var(--wp-rood)" }}>
          De cijfers van V8 zijn niet op te halen.
        </p>
      ) : leeg ? (
        <p className="wp-gz-uitleg" role="status">
          Nog geen beurten van V8 in de laatste 30 dagen.
        </p>
      ) : (
        <table className="wp-gz-mk-tabel wp-gz-v8-tabel">
          <thead>
            <tr>
              <th>Periode</th>
              <th className="wp-gz-mk-getal">Beurten</th>
              <th className="wp-gz-mk-getal">Handen</th>
              <th className="wp-gz-mk-getal">Kosten</th>
            </tr>
          </thead>
          <tbody>
            {data.perioden.map((periode) => (
              <tr key={periode.sleutel}>
                <td>
                  <span className="wp-gz-v8-periode">{periode.label}</span>
                  <small className="wp-gz-v8-datum">{bereik(periode)}</small>
                </td>
                <td className="wp-gz-mk-getal">
                  {periode.beurten}
                  {periode.mislukt > 0 ? (
                    <small className="wp-chip wp-chip-risico wp-gz-v8-mislukt">
                      {periode.mislukt} mislukt
                    </small>
                  ) : null}
                </td>
                <td className="wp-gz-mk-getal">{periode.handen}</td>
                <td className="wp-gz-mk-getal wp-gz-mk-bedrag">
                  {euro(periode.kosten_eur)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="wp-gz-uitleg">
        <UitlegTip
          tekst={UITLEG}
          kort="Zonder testbeurten."
          label="Wat telt V8 mee?"
        />
      </p>
    </div>
  );
}
